import { useState, useEffect } from 'react';
import { TeamState, Player } from '@/types';
import { checkRotationDeadlock } from '@/lib/validation';
import { ScoreBar } from '@/components/ScoreBar';

interface ServerSelectionModalProps {
  team: TeamState;
  eligibleServers: { eligible: Player[]; ineligible: { player: Player; remaining: number }[] };
  onSelect: (player: Player) => Promise<void>;
  onCancel?: () => void;
  reason: 'first-server' | 'service-break';
  onServeFoul?: (player: Player) => Promise<void>;
  onTimeout?: () => void;
  scoreTeamA?: TeamState;
  scoreTeamB?: TeamState;
  scoreA?: number;
  scoreB?: number;
  servingTeamId?: string;
}

export default function ServerSelectionModal({
  team,
  eligibleServers,
  onSelect,
  reason,
  onServeFoul,
  onTimeout,
  scoreTeamA,
  scoreTeamB,
  scoreA,
  scoreB,
  servingTeamId,
}: ServerSelectionModalProps) {
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serveFoulMode, setServeFoulMode] = useState(false);

  // Reset serve foul mode when the team changes (after a serve foul, service passes to the opponent)
  useEffect(() => {
    setServeFoulMode(false);
    setError(null);
  }, [team.id]);

  // Check for rotation deadlock
  const hasDeadlock = checkRotationDeadlock(team);

  // In serve foul mode every player is eligible (we just need to know who fouled)
  const effectiveEligible = serveFoulMode
    ? team.players
    : hasDeadlock
      ? team.players
      : eligibleServers.eligible;

  const handleSelect = async (player: Player) => {
    setSelectingId(player.id);
    setError(null);

    try {
      if (serveFoulMode && onServeFoul) {
        await onServeFoul(player);
        // Modal stays open; team will change and useEffect will reset serveFoulMode
      } else {
        await onSelect(player);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid selection');
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50">
      <div className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border rounded-t-2xl p-4 pb-safe max-h-[80vh] overflow-y-auto animate-slide-up">
        <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />

        {scoreTeamA && scoreTeamB && scoreA !== undefined && scoreB !== undefined && (
          <ScoreBar teamA={scoreTeamA} teamB={scoreTeamB} scoreA={scoreA} scoreB={scoreB} servingTeamId={servingTeamId} />
        )}

        <h2 className="text-lg font-bold text-text-primary text-center mb-1">
          {serveFoulMode
            ? 'WHO SERVED THE FOUL?'
            : reason === 'first-server'
              ? 'SELECT FIRST SERVER'
              : 'SELECT NEXT SERVER'}
        </h2>

        {serveFoulMode ? (
          <p className="text-error text-sm text-center mb-2">
            Select the player who committed the serve foul.
          </p>
        ) : reason === 'service-break' ? (
          <p className="text-text-secondary text-sm text-center mb-2">
            {team.name} now serve. Choose the server.
          </p>
        ) : null}

        {serveFoulMode && (
          <div className="bg-error/20 border border-error rounded-lg p-2 mb-4">
            <p className="text-sm text-error text-center">
              Opponent receives 1 point. Service changes to opponent.
            </p>
          </div>
        )}

        {!serveFoulMode && hasDeadlock && (
          <div className="bg-warning/20 border border-warning rounded-lg p-2 mb-4">
            <p className="text-sm text-warning text-center">
              Rotation cycle reset. All players eligible.
            </p>
          </div>
        )}

        {/* Team Header */}
        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: `${team.color}15`, borderColor: team.color, borderWidth: 1 }}
        >
          <p className="font-semibold text-center" style={{ color: team.color }}>
            {team.name.toUpperCase()}
          </p>
        </div>

        {/* Player Grid - Dark Themed Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {team.players.map((player) => {
            const isEligible = effectiveEligible.some((p) => p.id === player.id);
            const ineligibleInfo = !serveFoulMode
              ? eligibleServers.ineligible.find((i) => i.player.id === player.id)
              : undefined;
            const isSelected = selectingId === player.id;

            return (
              <button
                key={player.id}
                onClick={() => isEligible && handleSelect(player)}
                disabled={!isEligible || selectingId !== null}
                className={`py-5 px-3 rounded-lg font-bold text-xl transition-all ${isEligible ? 'active:scale-[0.98]' : 'cursor-not-allowed'}`}
                style={{
                  backgroundColor: isSelected ? team.color : isEligible ? 'rgb(39, 39, 42)' : 'rgb(63, 63, 70)',
                  color: isEligible ? '#ffffff' : '#9ca3af',
                  border: `4px solid ${isEligible ? team.color : '#52525b'}`,
                  boxShadow: isSelected ? `0 4px 12px ${team.color}60` : undefined,
                }}
              >
                {isSelected ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white rounded-full animate-spin" style={{ borderTopColor: 'transparent' }} />
                    {player.name}
                  </span>
                ) : (
                  <>
                    <span>{player.name}</span>
                    {!isEligible && ineligibleInfo && (
                      <span className="block text-sm mt-1 text-gray-400">
                        Cooldown ({ineligibleInfo.remaining} more)
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {error && (
          <div className="bg-error/20 border border-error rounded-lg p-3 mb-4">
            <p className="text-error text-sm text-center">{error}</p>
          </div>
        )}

        {serveFoulMode ? (
          <button
            onClick={() => { setServeFoulMode(false); setError(null); }}
            className="btn-secondary text-text-secondary"
          >
            BACK
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            {onServeFoul && (
              <button
                onClick={() => setServeFoulMode(true)}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border-2 border-error text-error font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                SERVE FOUL
              </button>
            )}
            {reason === 'service-break' && onTimeout && (
              <button
                onClick={onTimeout}
                disabled={selectingId !== null}
                className="w-full py-3 px-4 rounded-lg border-2 border-warning text-warning font-bold text-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                ⏱ TIMEOUT
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
